import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, User, X, History, Activity, Hand, Trash2, Filter } from 'lucide-react';
import AddPatient from './AddPatient';
import { apiFetch } from '../../utils/api';
import EditPatient from './EditPatient';
import ModalHeader from '../ModalHeader';

const Patients = () => {
  const navigate = useNavigate();
  const [showPatientForm, setShowPatientForm] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [showDeleteError, setShowDeleteError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [patients, setPatients] = useState([]);
  const [followUpsData, setFollowUpsData] = useState([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    diagnosis: [],
    otherDiagnosis: '',
    chiefComplaints: '',
    advice: '',
    vitals: { spo2: '', pr: '', bp: '', pefr: '' },
    refBy: ''
  });

  const [loadingPatients, setLoadingPatients] = useState(true);
  const [errorPatients, setErrorPatients] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);

  const fetchPatients = async () => {
    setLoadingPatients(true);
    setErrorPatients(null);
    try {
      const [patientsRes, followUpsRes] = await Promise.all([
        apiFetch(`${process.env.REACT_APP_CLIENT_BASE_URL}/api/patients`),
        apiFetch(`${process.env.REACT_APP_CLIENT_BASE_URL}/api/followup`)
      ]);
      if (!patientsRes.ok) throw new Error('Failed to fetch patients');
      const data = await patientsRes.json();
      setPatients(Array.isArray(data) ? data : data.patients || []);

      if (followUpsRes.ok) {
        const fuData = await followUpsRes.json();
        setFollowUpsData(fuData.data || []);
      }
    } catch (err) {
      setErrorPatients(err.message);
    } finally {
      setLoadingPatients(false);
    }
  };

  // Initial fetch only
  useEffect(() => {
    fetchPatients();
  }, []);

  const handleAddPatientClick = () => {
    setShowPatientForm(true);
  };

  const handleEditPatientClick = async (patient) => {
    try {
        const res = await fetch(`${process.env.REACT_APP_CLIENT_BASE_URL}/api/patients/${patient.patientId}`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
        const data = await res.json();
        if (data && data.patient) {
            setSelectedPatient(data.patient);
        } else if (data) {
            setSelectedPatient(data);
        } else {
            setSelectedPatient(patient); // fallback
        }
        setShowEditModal(true);
    } catch (err) {
        console.error("Failed to fetch full patient details", err);
        setSelectedPatient(patient);
        setShowEditModal(true);
    }
  };

  const handlePatientSuccess = () => {
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 3000);
    fetchPatients(); // Refresh patients list after successful operation
  };

  const handlePatientError = () => {
    setShowErrorMessage(true);
    setTimeout(() => setShowErrorMessage(false), 3000);
  };

  const handleDeletePatient = async (patientId) => {
    if (!window.confirm('Are you sure you want to delete this patient and all related data? This action cannot be undone.')) return;
    try {
      const res = await apiFetch(`${process.env.REACT_APP_CLIENT_BASE_URL}/api/patients/${patientId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete patient');
      setShowDeleteSuccess(true);
      setTimeout(() => setShowDeleteSuccess(false), 3000);
      fetchPatients();
    } catch (err) {
      setShowDeleteError(true);
      setTimeout(() => setShowDeleteError(false), 3000);
    }
  };

  const DIAGNOSIS_OPTIONS = [
    { key: 'bronchialAsthma', label: 'Bronchial Asthma' },
    { key: 'copd', label: 'COPD' },
    { key: 'bronchiectasis', label: 'Bronchiectasis' },
    { key: 'rhinitis', label: 'Rhinitis' },
    { key: 'sinusitis', label: 'Sinusitis' },
    { key: 'drugAllergy', label: 'Drug Allergy' },
    { key: 'conjunctivitis', label: 'Conjunctivitis' },
    { key: 'eczema', label: 'Eczema' },
    { key: 'urticaria', label: 'Urticaria' }
  ];

  const filteredPatients = patients.filter(patient => {
    // 1. Basic Search Query
    const matchesSearch = patient.basicInfo?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          patient.patientId?.toLowerCase().includes(searchQuery.toLowerCase());
    if (!matchesSearch) return false;

    
    // Date Filter (based on patient.createdAt or fallback to patient.basicInfo.date)
    if (dateFilter.start || dateFilter.end) {
      const patientDateStr = patient.createdAt || patient.date || (patient.basicInfo && patient.basicInfo.date);
      if (patientDateStr) {
        const patientDate = new Date(patientDateStr);
        patientDate.setHours(0, 0, 0, 0);

        if (dateFilter.start) {
          const start = new Date(dateFilter.start);
          start.setHours(0, 0, 0, 0);
          if (patientDate < start) return false;
        }
        if (dateFilter.end) {
          const end = new Date(dateFilter.end);
          end.setHours(0, 0, 0, 0);
          if (patientDate > end) return false;
        }
      }
    }

    if (!showAdvancedFilters) return true;

    // Reference Doctor Filter
    if (filters.refBy) {
      const refBy = patient.basicInfo?.refBy?.toLowerCase() || '';
      if (!refBy.includes(filters.refBy.toLowerCase())) return false;
    }



    // 4. Follow Up Checks
    const hasFollowUpFilters = filters.chiefComplaints || filters.advice || 
      filters.vitals.spo2 || filters.vitals.pr || filters.vitals.bp || filters.vitals.pefr;

    if (hasFollowUpFilters) {
      const patientFu = followUpsData.find(fu => 
         fu.patientId?._id === patient._id || fu.patientId === patient._id
      );
      
      if (!patientFu) return false;

      const visitsCount = patientFu.followUpInstructions?.length || 0;
      let matchesAnyVisit = false;
      
      for (let i = 0; i < visitsCount; i++) {
        let visitMatches = true;

        if (filters.chiefComplaints) {
          const notes = patientFu.followUpInstructions?.[i]?.toLowerCase() || '';
          if (!notes.includes(filters.chiefComplaints.toLowerCase())) visitMatches = false;
        }
        if (filters.advice) {
          const advice = patientFu.advice?.[i]?.toLowerCase() || '';
          if (!advice.includes(filters.advice.toLowerCase())) visitMatches = false;
        }
        if (filters.vitals.spo2) {
          const spo2 = patientFu.SPO2?.[i]?.toLowerCase() || '';
          if (!spo2.includes(filters.vitals.spo2.toLowerCase())) visitMatches = false;
        }
        if (filters.vitals.pr) {
          const pr = patientFu.PR?.[i]?.toLowerCase() || '';
          if (!pr.includes(filters.vitals.pr.toLowerCase())) visitMatches = false;
        }
        if (filters.vitals.bp) {
          const bp = patientFu.BP?.[i]?.toLowerCase() || '';
          if (!bp.includes(filters.vitals.bp.toLowerCase())) visitMatches = false;
        }
        if (filters.vitals.pefr) {
          const pefr = patientFu.PEFR?.[i]?.toLowerCase() || '';
          if (!pefr.includes(filters.vitals.pefr.toLowerCase())) visitMatches = false;
        }

        if (visitMatches) {
          matchesAnyVisit = true;
          break;
        }
      }

      if (!matchesAnyVisit) return false;
    }

    return true;
  });

  return (
    <>
      {/* Success Message */}
      {showSuccessMessage && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: '#10B981',
            color: 'white',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <span>Patient data successfully saved!</span>
        </div>
      )}

      {/* Delete Success Message */}
      {showDeleteSuccess && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: '#10B981',
            color: 'white',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <span>Patient data successfully deleted!</span>
        </div>
      )}

      {/* Error Message */}
      {showErrorMessage && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: '#EF4444',
            color: 'white',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>Error saving patient data. Please try again.</span>
        </div>
      )}

      {/* Delete Error Message */}
      {showDeleteError && (
        <div
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            backgroundColor: '#EF4444',
            color: 'white',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>Error deleting patient data. Please try again.</span>
        </div>
      )}

      {/* Patients Table */}
      <div className="d-flex gap-4 flex-wrap">
        {/* Add Patient Card */}
        <div className="card shadow-sm" style={{ flex: 1, minWidth: 320 }}>
          <div className="card-header bg-white">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="card-title mb-1 fw-semibold">Add Patient</h5>
                <p className="text-muted mb-0 small">Start by adding your first patient</p>
              </div>
            </div>
          </div>

          <div className="card-body p-5">
            <div
              style={{
                minHeight: '200px',
                border: '2px dashed #d1d5db',
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                backgroundColor: '#fafafa',
                transition: 'all 0.3s ease-in-out'
              }}
              onClick={handleAddPatientClick}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#2563eb';
                e.currentTarget.style.backgroundColor = '#f8fafc';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.backgroundColor = '#fafafa';
              }}
            >
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '16px'
                }}
              >
                <Plus size={32} style={{ color: '#6b7280' }} />
              </div>
              <h6 className="fw-semibold text-dark mb-2">New Patient</h6>
              <p className="text-muted mb-0 text-center" style={{ maxWidth: '300px' }}>
                Click here to add patient details and start managing their information
              </p>
            </div>
          </div>
        </div>

        {/* Edit Patient Card */}
        <div className="card shadow-sm" style={{ flex: 1, minWidth: 320 }}>
          <div className="card-header bg-white">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h5 className="card-title mb-1 fw-semibold">Edit Patient</h5>
                <p className="text-muted mb-0 small">Search and edit existing patient details</p>
              </div>
            </div>
          </div>
          <div className="card-body p-5">
            <div className="input-group mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="Search by name or ID..."
                style={{ borderRadius: '8px 0 0 8px', fontSize: 15 }}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              <span className="input-group-text" style={{ borderRadius: '0 8px 8px 0', borderLeft: 0, background: '#f3f4f6' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" stroke="#6b7280" strokeWidth="2" /><path stroke="#6b7280" strokeWidth="2" strokeLinecap="round" d="M21 21l-3.5-3.5" /></svg>
              </span>
            </div>
            {searchQuery && (
              <div style={{ maxHeight: 200, overflowY: 'auto', marginTop: 8 }}>
                {filteredPatients.length === 0 ? (
                  <div className="text-muted">No patients found.</div>
                ) : (
                  <ul className="list-group">
                    {filteredPatients.map((patient) => (
                      <li key={patient._id} className="list-group-item d-flex justify-content-between align-items-center" style={{ fontSize: 15 }}>
                        <div>
                          <span style={{ fontWeight: 600, color: '#2563eb' }}>{patient.patientId}</span>
                          <span style={{ marginLeft: 12 }}>{patient.basicInfo?.name}</span>
                        </div>
                        <button
                          className="btn btn-outline-primary btn-sm"
                          style={{ borderRadius: 8, fontWeight: 500, fontSize: 14 }}
                          onClick={() => handleEditPatientClick(patient)}
                        >
                          Edit Details
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Patients List */}
      <div className="card shadow-sm mt-4">
        <div className="card-header bg-white d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 className="mb-0 fw-bold text-dark" style={{ fontSize: '16px' }}>Patients List</h5>
          <div className="d-flex gap-2 flex-nowrap align-items-center">
            {/* Date Filters */}
            <div className="d-flex align-items-center gap-2" style={{ backgroundColor: '#f8fafc', padding: '4px 8px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <span className="text-secondary fw-semibold" style={{ fontSize: '13px' }}>Date:</span>
              <input
                type="date"
                className="form-control form-control-sm border-0 bg-transparent shadow-none"
                style={{ fontSize: '13px', padding: '2px 4px', width: '115px', color: '#475569' }}
                value={dateFilter.start}
                onChange={e => setDateFilter(prev => ({ ...prev, start: e.target.value }))}
                title="Start Date"
              />
              <span className="text-muted" style={{ fontSize: '12px' }}>to</span>
              <input
                type="date"
                className="form-control form-control-sm border-0 bg-transparent shadow-none"
                style={{ fontSize: '13px', padding: '2px 4px', width: '115px', color: '#475569' }}
                value={dateFilter.end}
                onChange={e => setDateFilter(prev => ({ ...prev, end: e.target.value }))}
                title="End Date"
              />
              {(dateFilter.start || dateFilter.end) && (
                <button 
                  className="btn btn-sm btn-link text-danger p-0 ms-1"
                  onClick={() => setDateFilter({ start: '', end: '' })}
                  title="Clear Dates"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            <button 
              className={`btn ${showAdvancedFilters ? 'btn-primary' : 'btn-outline-secondary'} d-flex align-items-center gap-1.5`}
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              style={{ borderRadius: 8, fontWeight: 500, fontSize: 14, whiteSpace: 'nowrap' }}
            >
              <Filter size={16} />
              Filters
            </button>
            <div className="input-group" style={{ width: 260 }}>
              <input
                type="text"
                className="form-control"
                placeholder="Search name or ID..."
                style={{ borderRadius: '8px 0 0 8px', fontSize: 14 }}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
              <span className="input-group-text" style={{ borderRadius: '0 8px 8px 0', borderLeft: 0, background: '#f3f4f6' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="none" viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" stroke="#6b7280" strokeWidth="2" /><path stroke="#6b7280" strokeWidth="2" strokeLinecap="round" d="M21 21l-3.5-3.5" /></svg>
              </span>
            </div>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="bg-light border-bottom px-4 py-3" style={{ backgroundColor: '#f8fafc' }}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="fw-bold text-primary d-flex align-items-center gap-1.5" style={{ fontSize: 14 }}>
                <Filter size={16} /> Advanced Patient Filters
              </span>
              <button 
                className="btn btn-sm btn-link text-danger fw-semibold text-decoration-none"
                style={{ fontSize: 13, padding: 0 }}
                onClick={() => setFilters({
                  diagnosis: [], otherDiagnosis: '', chiefComplaints: '', advice: '', vitals: { spo2: '', pr: '', bp: '', pefr: '' }, refBy: ''
                })}
              >
                Clear All Filters
              </button>
            </div>
            
            <div className="row g-4">
              {/* Clinical Notes */}
              <div className="col-12 col-lg-8">
                <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Clinical Notes</label>
                <div className="row g-3">
                  <div className="col-12 col-md-6">
                    <textarea 
                      className="form-control shadow-sm" 
                      placeholder="Search Chief Complaints..." 
                      rows="2"
                      style={{ borderRadius: 8, fontSize: 13, border: '1px solid #e2e8f0', background: '#fff', resize: 'none' }}
                      value={filters.chiefComplaints}
                      onChange={e => setFilters(prev => ({ ...prev, chiefComplaints: e.target.value }))}
                    />
                  </div>
                  <div className="col-12 col-md-6">
                    <textarea 
                      className="form-control shadow-sm" 
                      placeholder="Search Advice & Instructions..." 
                      rows="2"
                      style={{ borderRadius: 8, fontSize: 13, border: '1px solid #e2e8f0', background: '#fff', resize: 'none' }}
                      value={filters.advice}
                      onChange={e => setFilters(prev => ({ ...prev, advice: e.target.value }))}
                    />
                  </div>
                </div>
                                <div className="row mt-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Reference Doctor</label>
                      <input 
                        type="text"
                        className="form-control shadow-sm"
                        placeholder="Search by Reference Doctor..."
                        style={{ borderRadius: 8, fontSize: 13, border: '1px solid #e2e8f0', background: '#fff' }}
                        value={filters.refBy}
                        onChange={e => setFilters(prev => ({ ...prev, refBy: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
  
                {/* Vitals */}
              <div className="col-12 col-lg-4">
                <label className="form-label fw-bold text-secondary mb-2" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>Patient Vitals</label>
                <div className="row g-2">
                  <div className="col-6">
                    <input 
                      type="text" 
                      className="form-control form-control-sm shadow-sm" 
                      placeholder="SPO2 %" 
                      style={{ borderRadius: 8, fontSize: 13, height: 32, border: '1px solid #e2e8f0', background: '#fff' }}
                      value={filters.vitals.spo2}
                      onChange={e => setFilters(prev => ({ ...prev, vitals: { ...prev.vitals, spo2: e.target.value } }))}
                    />
                  </div>
                  <div className="col-6">
                    <input 
                      type="text" 
                      className="form-control form-control-sm shadow-sm" 
                      placeholder="Pulse / PR" 
                      style={{ borderRadius: 8, fontSize: 13, height: 32, border: '1px solid #e2e8f0', background: '#fff' }}
                      value={filters.vitals.pr}
                      onChange={e => setFilters(prev => ({ ...prev, vitals: { ...prev.vitals, pr: e.target.value } }))}
                    />
                  </div>
                  <div className="col-6">
                    <input 
                      type="text" 
                      className="form-control form-control-sm shadow-sm" 
                      placeholder="BP (120/80)" 
                      style={{ borderRadius: 8, fontSize: 13, height: 32, border: '1px solid #e2e8f0', background: '#fff' }}
                      value={filters.vitals.bp}
                      onChange={e => setFilters(prev => ({ ...prev, vitals: { ...prev.vitals, bp: e.target.value } }))}
                    />
                  </div>
                  <div className="col-6">
                    <input 
                      type="text" 
                      className="form-control form-control-sm shadow-sm" 
                      placeholder="PEFR" 
                      style={{ borderRadius: 8, fontSize: 13, height: 32, border: '1px solid #e2e8f0', background: '#fff' }}
                      value={filters.vitals.pefr}
                      onChange={e => setFilters(prev => ({ ...prev, vitals: { ...prev.vitals, pefr: e.target.value } }))}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="card-body">
          {loadingPatients && <div>Loading patients...</div>}
          {errorPatients && <div className="text-danger">{errorPatients}</div>}
          {!loadingPatients && !errorPatients && patients.length === 0 && (
            <div className="text-muted">No patients found.</div>
          )}
          {!loadingPatients && !errorPatients && patients.length > 0 && (
            <div className="table-responsive">
              <table className="table align-middle" style={{ minWidth: 700, fontSize: 15, width: '100%' }}>
                <thead style={{ background: '#f8fafc' }}>
                  <tr>
                    <th style={{ padding: '14px 16px', width: '10%' }}>Patient ID</th>
                    <th style={{ padding: '14px 16px', width: '13%' }}>Name</th>
                    <th style={{ padding: '14px 16px', width: '5%' }}>Age</th>
                    <th style={{ padding: '14px 16px', width: '5%' }}>Sex</th>
                    <th style={{ padding: '14px 16px', width: '43%', minWidth: 300, textAlign: 'left' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPatients.map((patient) => (
                    <tr key={patient._id}>
                      <td style={{ padding: '12px 16px', verticalAlign: 'middle', fontWeight: 500, color: '#2563eb' }}>{patient.patientId}</td>
                      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>{patient.basicInfo?.name}</td>
                      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>{patient.basicInfo?.age}</td>
                      <td style={{ padding: '12px 16px', verticalAlign: 'middle' }}>
                        <span className="badge rounded-pill" style={{ background: '#e0e7ff', color: '#3730a3', fontWeight: 500, fontSize: 14 }}>
                          {patient.basicInfo?.sex}
                        </span>
                      </td>
                      <td style={{ padding: '10px 8px', verticalAlign: 'middle', textAlign: 'left', minWidth: 220 }}>
                        <div className="d-flex flex-wrap gap-2">
                          <button
                            className="btn btn-primary btn-sm patient-action-btn"
                            onClick={() => navigate(`/patients/${patient.patientId}`)}
                            style={{ minWidth: 90, fontWeight: 600, letterSpacing: 0.5, borderRadius: 8, boxShadow: '0 1px 2px rgba(37,99,235,0.08)', transition: 'background 0.2s, color 0.2s' }}
                          >
                            <User size={20} />
                            View
                          </button>
                          <button
                            className="btn btn-warning btn-sm patient-action-btn"
                            onClick={() => navigate(`/patients/${patient.patientId}/test`)}
                            style={{
                              minWidth: 120,
                              fontWeight: 600,
                              letterSpacing: 0.5,
                              borderRadius: 8,
                              boxShadow: '0 1px 2px rgba(251,191,36,0.08)',
                              color: '#fff',
                              background: '#f59e42',
                              border: 'none',
                              transition: 'background 0.2s, color 0.2s'
                            }}
                          >
                            <Activity size={20} className="me-1" />
                            Allergy Test
                          </button>
                          <button
                            className="btn btn-success btn-sm patient-action-btn"
                            onClick={() => navigate(`/patients/${patient.patientId}/patch-test`)}
                            style={{
                              minWidth: 120,
                              fontWeight: 600,
                              letterSpacing: 0.5,
                              borderRadius: 8,
                              boxShadow: '0 1px 2px rgba(34,197,94,0.08)',
                              color: '#fff',
                              background: '#10b981',
                              border: 'none',
                              transition: 'background 0.2s, color 0.2s'
                            }}
                          >
                            <Activity size={20} className="me-1" />
                            Patch Test
                          </button>
                          <button
                            className="btn btn-info btn-sm patient-action-btn"
                            onClick={() => {
                              navigate(`/patients/${patient.patientId}/add-instruction`);
                              window.scrollTo(0, 0);
                            }}
                            style={{
                              minWidth: 120,
                              fontWeight: 600,
                              letterSpacing: 0.5,
                              borderRadius: 8,
                              boxShadow: '0 1px 2px rgba(59,130,246,0.08)',
                              color: '#fff',
                              background: '#3b82f6',
                              border: 'none',
                              transition: 'background 0.2s, color 0.2s'
                            }}
                          >
                            <span className="me-1" style={{ display: 'inline-flex', verticalAlign: 'middle' }}>
                              <svg width="22" height="22" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect x="4" y="4" width="20" height="24" rx="2.5" stroke="currentColor" strokeWidth="2.2"/>
                                <circle cx="8.5" cy="8.5" r="1.2" fill="currentColor" />
                                <rect x="11" y="7" width="7" height="1.7" rx="0.85" fill="currentColor" />
                                <rect x="8" y="12" width="10" height="1.7" rx="0.85" fill="currentColor" />
                                <rect x="8" y="16" width="7" height="1.7" rx="0.85" fill="currentColor" />
                                <rect x="8" y="20" width="7" height="1.7" rx="0.85" fill="currentColor" />
                                <rect x="8" y="24" width="7" height="1.7" rx="0.85" fill="currentColor" />
                                <path d="M16 19v5.5c0 3.5 2.5 5.5 5 5.5s5-2 5-5.5V21a2 2 0 0 0-2-2c-1.1 0-2 .9-2 2v-4a2 2 0 0 0-2-2c-1.1 0-2 .9-2 2v2z" stroke="currentColor" strokeWidth="2.2" fill="none"/>
                              </svg>
                            </span>
                            Instruction
                          </button>
                          <button
                            className="btn btn-danger btn-sm patient-action-btn"
                            onClick={() => handleDeletePatient(patient.patientId)}
                            style={{
                              minWidth: 120,
                              fontWeight: 600,
                              letterSpacing: 0.5,
                              borderRadius: 8,
                              boxShadow: '0 1px 2px rgba(239,68,68,0.08)',
                              color: '#fff',
                              background: '#ef4444',
                              border: 'none',
                              transition: 'background 0.2s, color 0.2s'
                            }}
                          >
                            <Trash2 size={18} className="me-1" />
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add global style for hover effect */}
      <style>{`
        .patient-action-btn:hover {
          filter: brightness(0.95);
          transform: translateY(-2px) scale(1.03);
          box-shadow: 0 4px 12px rgba(37,99,235,0.10);
        }
      `}</style>

      {showPatientForm && (
        <AddPatient
          onClose={() => setShowPatientForm(false)}
          onSuccess={handlePatientSuccess}
          onError={handlePatientError}
        />
      )}

      {showEditModal && selectedPatient && (
        <EditPatient
          patient={selectedPatient}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPatient(null);
          }}
          onSuccess={handlePatientSuccess}
          onError={handlePatientError}
        />
      )}
    </>
  );
};

export default Patients;